import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, Phone, DollarSign, Smile, PlugZap } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { formatCurrency, formatHours } from '../../lib/format.js';
import type { AnalyticsPoint, ProfitabilityRow } from '../../api/types.js';

type ProfType = 'company' | 'tech' | 'board';

function SimpleBarChart({ data, color = 'bg-brand-500', label }: { data: AnalyticsPoint[]; color?: string; label?: string }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-2">
      {label && <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">{label}</p>}
      <div className="flex items-end gap-1.5 h-32">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex flex-col justify-end" style={{ height: '100px' }}>
              <div
                className={`w-full rounded-t-sm ${color} transition-all duration-500`}
                style={{ height: `${(d.value / max) * 100}%`, minHeight: '2px' }}
                title={`${d.label}: ${d.value}`}
              />
            </div>
            <span className="text-[10px] text-[hsl(var(--content-subtle))] font-medium">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function friendlyError(error: string): string {
  if (error.includes('403')) return 'Analytics requires the dispatcher, admin or owner role.';
  if (error.includes('401')) return 'Your session expired — log out and back in.';
  return `${error} — try refreshing.`;
}

function SentimentPie({ data }: { data: AnalyticsPoint[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return <p className="text-sm text-[hsl(var(--content-subtle))] py-6 text-center">No analyzed calls in the last 30 days.</p>;
  }
  const colors = ['bg-red-500', 'bg-orange-500', 'bg-gray-400', 'bg-green-400', 'bg-emerald-500'];
  const emojis = ['😡', '😟', '😐', '🙂', '😊'];

  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-sm">{emojis[i]}</span>
          <div className="flex-1 h-5 rounded-full bg-[hsl(var(--surface-subtle))] overflow-hidden">
            <div
              className={`h-full rounded-full ${colors[i]} transition-all duration-700`}
              style={{ width: `${(d.value / total) * 100}%` }}
            />
          </div>
          <div className="flex items-center gap-1 w-20 justify-end">
            <span className="text-xs font-medium text-[hsl(var(--content))]">{d.value}</span>
            <span className="text-xs text-[hsl(var(--content-subtle))]">({Math.round((d.value / total) * 100)}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AnalyticsPage() {
  const navigate = useNavigate();
  // 3CX connection state (GET /api/v1/calls/status) so empty call charts explain why.
  const { data: callsStatus } = useGet<{ threecx: { connected: boolean; status: string } }>('/api/v1/calls/status');
  // API contract (apps/api/src/routes/analytics.ts GET /analytics/calls):
  // volume/sentiment are {date, value} daily series; sentimentDist is the
  // 1-5 bucket distribution the pie chart renders.
  const { data: analyticsData, loading: aLoading, error: aError, refetch: aRefetch } = useGet<{
    volume: Array<{ date: string; value: number }>;
    sentiment: Array<{ date: string; value: number }>;
    sentimentDist: AnalyticsPoint[];
    avgDurationSec: number;
    totalCalls: number;
    avgSentiment: number;
    p1Calls: number;
  }>('/api/v1/analytics/calls');

  const volumePoints: AnalyticsPoint[] = (analyticsData?.volume ?? []).map(p => ({
    label: p.date.slice(5), // MM-DD
    value: p.value,
  }));
  const sentimentPoints: AnalyticsPoint[] = analyticsData?.sentimentDist ?? [];

  const [profType, setProfType] = React.useState<ProfType>('company');
  // API contract (apps/api/src/routes/analytics.ts): ?dimension= with response
  // { overall, groups: [{key, label, summary}] }. Map groups into the flat
  // table rows the console renders.
  const { data: profData, loading: pLoading, error: pError, refetch: pRefetch } = useGet<{
    overall: unknown;
    groups: Array<{ key: string; label: string; summary: {
      hours: number; revenue: number; laborCost: number; margin: number; marginPct: number; entryCount: number; missingRateCount: number; missingCostCount: number;
    } }>;
  }>(
    `/api/v1/analytics/profitability?dimension=${profType === 'tech' ? 'technician' : profType}`,
    [profType]
  );

  const profRows: ProfitabilityRow[] = (profData?.groups ?? []).map(g => ({
    entity: g.label,
    type: profType,
    revenue: g.summary.revenue,
    cost: g.summary.laborCost,
    margin: g.summary.marginPct * 100,
    ticketCount: g.summary.entryCount,
    hours: g.summary.hours,
    revenueAvailable: (g.summary.missingRateCount ?? 0) === 0,
    costAvailable: (g.summary.missingCostCount ?? 0) === 0,
  }));

  const noCalls = !!analyticsData && analyticsData.totalCalls === 0;
  const threecxDown = !!callsStatus && !callsStatus.threecx.connected;
  const hasSentiment = !!analyticsData && analyticsData.sentimentDist.some(d => d.value > 0);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Analytics</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">Call volume, sentiment and duration (last 30 days), plus profitability from ConnectWise time entries (last 12 months).</p>
      </div>

      {aError && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10 px-4 py-3 text-sm text-red-700 dark:text-red-300 flex items-center gap-3">
          <span className="flex-1">Couldn't load call analytics: {friendlyError(aError)}</span>
          <button onClick={aRefetch} className="text-xs font-semibold underline">Retry</button>
        </div>
      )}

      {threecxDown && noCalls && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-3">
          <PlugZap className="h-4 w-4 shrink-0" />
          <span className="flex-1">3CX is not connected, so there is no call data. Connect 3CX in Admin → Integrations to populate call analytics.</span>
          <button
            onClick={() => navigate('/admin/integrations')}
            className="shrink-0 rounded-lg bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-700"
          >
            Open Integrations
          </button>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Calls (30d)', value: analyticsData?.totalCalls ?? '—', icon: Phone, color: 'text-blue-600' },
          { label: 'Calls on open P1s', value: analyticsData?.p1Calls ?? '—', icon: BarChart3, color: 'text-red-600' },
          { label: 'Avg Duration', value: analyticsData && !noCalls ? `${Math.floor(analyticsData.avgDurationSec / 60)}m ${analyticsData.avgDurationSec % 60}s` : '—', icon: TrendingUp, color: 'text-green-600' },
          { label: 'Avg Sentiment', value: analyticsData && hasSentiment ? `${analyticsData.avgSentiment.toFixed(1)} / 5` : '—', icon: Smile, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3 flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--surface-subtle))]">
              <s.icon className={`h-4 w-4 ${s.color}`} />
            </div>
            <div>
              <p className={`text-xl font-bold tabular-nums ${s.color}`}>{aLoading ? '…' : s.value}</p>
              <p className="text-xs text-[hsl(var(--content-subtle))]">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Call volume */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-4">Call Volume — Last 30 Days</h3>
          {aLoading ? <CardSkeleton lines={4} /> : volumePoints.length > 0 ? (
            <SimpleBarChart data={volumePoints} color="bg-brand-500" />
          ) : (
            <p className="text-sm text-[hsl(var(--content-subtle))] py-10 text-center">{aError ? 'Unavailable.' : 'No calls in the last 30 days.'}</p>
          )}
          <p className="text-xs text-[hsl(var(--content-placeholder))] mt-2">Total calls per day</p>
        </div>

        {/* Sentiment distribution */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-4">Sentiment Distribution</h3>
          {aLoading ? <CardSkeleton lines={5} /> : analyticsData ? (
            <SentimentPie data={sentimentPoints} />
          ) : (
            <p className="text-sm text-[hsl(var(--content-subtle))] py-10 text-center">Unavailable.</p>
          )}
        </div>
      </div>

      {/* Profitability */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Profitability</h3>
          </div>
          <div className="ml-auto flex p-1 gap-1 bg-[hsl(var(--surface-subtle))] rounded-xl border border-[hsl(var(--border))]">
            {(['company', 'tech', 'board'] as ProfType[]).map(t => (
              <button
                key={t}
                onClick={() => setProfType(t)}
                className={cn(
                  'px-3 py-1 rounded-lg text-xs font-medium capitalize transition-colors',
                  profType === t
                    ? 'bg-[hsl(var(--surface))] text-[hsl(var(--content))] shadow-sm'
                    : 'text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]'
                )}
              >
                By {t}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]">
                {['Name', 'Revenue', 'Cost', 'Margin', 'Time entries', 'Hours'].map(h => (
                  <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {pLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="py-3 px-4">
                        <div className="h-4 w-20 rounded bg-[hsl(var(--surface-subtle))] animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : pError ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon="⚠️"
                      title="Couldn't load profitability"
                      description={friendlyError(pError)}
                      action={<button onClick={pRefetch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Try again</button>}
                    />
                  </td>
                </tr>
              ) : profRows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      icon="💵"
                      title="No billable time in the last 12 months"
                      description="Profitability is computed from ConnectWise time entries synced by the worker, priced with technician and company billing rates."
                    />
                  </td>
                </tr>
              ) : (
                [...profRows].sort((a, b) => b.margin - a.margin).map(row => (
                  <tr key={row.entity} className="hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                    <td className="py-3 px-4 font-medium text-[hsl(var(--content))]">{row.entity}</td>
                    <td className="py-3 px-4 text-green-600 font-medium tabular-nums">{row.revenueAvailable ? formatCurrency(row.revenue) : "Unavailable"}</td>
                    <td className="py-3 px-4 text-red-600 font-medium tabular-nums">{row.costAvailable ? formatCurrency(row.cost) : "Unavailable"}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 rounded-full bg-[hsl(var(--surface-subtle))]">
                          <div
                            className={`h-full rounded-full ${row.margin >= 35 ? 'bg-green-500' : row.margin >= 25 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(row.margin, 50) * 2}%` }}
                          />
                        </div>
                        <span className={`text-xs font-bold tabular-nums ${row.margin >= 35 ? 'text-green-600' : row.margin >= 25 ? 'text-amber-600' : 'text-red-600'}`}>
                          {row.revenueAvailable && row.costAvailable ? `${row.margin.toFixed(1)}%` : "Unavailable"}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[hsl(var(--content-subtle))] tabular-nums">{row.ticketCount}</td>
                    <td className="py-3 px-4 text-[hsl(var(--content-subtle))] tabular-nums" title="Total billable time (nearest 15 min)">{formatHours(row.hours)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
